import React, { useState, useEffect, useRef } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button
} from "reactstrap"
import ClipLoader from "react-spinners/ClipLoader"
import axios from "axios"
import * as AxisUtility from "helpers/camera_utility"
import * as HanwhaUtility from "helpers/hanwha_camera_utility"
import NoteModal from "./NoteModal"
import Buttons from "./Buttons"
import "./newMeasurementModal.css"
import SetupRecording from "./NewMeasurementModalPages/SetupRecording"
import BeforeRecording from "./NewMeasurementModalPages/BeforeRecording"
import DuringRecording from "./NewMeasurementModalPages/DuringRecording"
import AfterRecording from "./NewMeasurementModalPages/AfterRecording"
import BloodSugarLevel from "./NewMeasurementModalPages/BloodSugarLevel"
import BloodPressureAnnotation from "./NewMeasurementModalPages/BloodPressureAnnotation"
import {v4 as uuidv4} from 'uuid';
import WarningAlert from "../WarningAlert";

const BASE_URL = process.env.REACT_APP_BASE_URL
const ENV_RESOURCE = process.env.REACT_APP_ENV_RESOURCE

const NewMeasurementModal = ({
  isOpen,
  toggle,
  devices,
  cameras,
  selectedSubject,
  onConfirm,
  modalLoading,
  setModalLoading,
  messages,
  currentPage,
  setCurrentPage,
  measurementData,
  onSettingChange,
  setDashboardData,
  noSubjects,
  annotationMessages,
  setAnnotationMessages
}) => {
  const [result, setResult] = useState(null)
  const [showCloseButton, setShowCloseButton] = useState(false)
  const [timer, setTimer] = useState(0)
  const [recordingProfileIDsMap, setRecordingProfileIDsMap] = useState({})
  const [bloodModalLoading, setBloodModalLoading] = useState(false)
  const [hasMeasured, setHasMeasured] = useState(null)
  const [lastAte, setLastAte] = useState("")
  const [glucoseLevel, setGlucoseLevel] = useState("")
  const [errors, setErrors] = useState([])
  const [showNoteModal, setShowNoteModal] = useState(false)
  const [noteModalTitle, setNoteModalTitle] = useState("")
  const [systolic, setSystolic] = useState("")
  const [diastolic, setDiastolic] = useState("")
  const [recordingId, setRecordingId] = useState(null);

  const timerRef = useRef(null)
  const startTimeRef = useRef(null)

  const { component: warningComponent, checks } = WarningAlert({
    devices: devices,
    cameras: cameras,
    selectedSubject,
    noSubjects
  });

  const handleProceed = async () => {
    if (ENV_RESOURCE === "dca-master") {
      const hasBluetoothEnabled = devices.some(
        device => device.bleDevice === "On"
      )
      if (!hasBluetoothEnabled) {
        setResult({
          success: false,
          message:
            "At least one device must have Bluetooth enabled to start the measurement.",
        })
        return
      }
    }
    setModalLoading(true)
    try {
      const res = await onConfirm(recordingId)
      setResult({
        success: true,
        message: "Devices are preparing for the measurement!",
      })
    } catch (error) {
      setResult({
        success: false,
        message: error.message || "Failed to prepare measurement",
      })
    }
    setModalLoading(false)
    setCurrentPage(2)
  }


  const handleProceedLast = () => {
    setCurrentPage(5)
  } 

  useEffect(() => {
    if (!isOpen) {
      setResult(null)
      setCurrentPage(0)
      setShowCloseButton(false)
      setTimer(0)
      setAnnotationMessages({})
      cancelAnimationFrame(timerRef.current)
      setRecordingId(null) 
    } else {
      setRecordingId(uuidv4())
    }
  }, [isOpen])

  useEffect(() => {
    if (currentPage === 2 && result?.success) {
      cancelAnimationFrame(timerRef.current)
      setTimer(0)
    }
  }, [currentPage, result])

  useEffect(() => {
    if (currentPage === 3) {
      startTimeRef.current = Date.now() - timer * 1000

      const updateTimer = () => {
        setTimer(Math.floor((Date.now() - startTimeRef.current) / 1000))
        timerRef.current = requestAnimationFrame(updateTimer)
      }

      timerRef.current = requestAnimationFrame(updateTimer)
    }

    return () => {
      cancelAnimationFrame(timerRef.current)
    }
  }, [currentPage])


  const handleStart = async () => {
    console.log("Starting measurement...")
    setModalLoading(true)

    const mobileDeviceInfo = devices.map(device => ({
      connectionId: device.connectionId,
      camera: device.camera,
      bleEnabled: device.bleDevice === "On",
      mode : device.mode,
      ppgDeviceOn: device.ppgDeviceOn
    }))

    const requestBody = {
      subjectId: measurementData.subjectId,
      recordingId: measurementData.recordingId,
      mobileDeviceInfo: mobileDeviceInfo,
    }

    try {
      const response = await axios.post(
        `${BASE_URL}web/measurementStart`,
        requestBody
      )
      console.log("Measurement started:", response.data)

      cameras.forEach(async camera => {
        const hostname = camera.details.hostname
        if (camera.type === "axis") {
          console.log("Starting recording for camera", hostname)
          const recordingId = await AxisUtility.startRecording(hostname, "NetworkShare", "resolution%3D1920x1080%26videocodec%3Dh265%26camera%3D1")
          setRecordingProfileIDsMap(prev => ({
            ...prev,
            [hostname]: recordingId,
          }))
        } else if (camera.type === "hanwha") {
          console.log("Starting recording for camera", hostname)
          await HanwhaUtility.startHanwhaRecording(camera.details.hostname);
        }
      })


      setResult({
        success: true,
        message: "Measurement started successfully!",
      })
      setDashboardData(response.data.devices)
      setCurrentPage(3)

      startTimeRef.current = Date.now()
      const updateTimer = () => {
        setTimer(Math.floor((Date.now() - startTimeRef.current) / 1000))
        timerRef.current = requestAnimationFrame(updateTimer)
      }
      timerRef.current = requestAnimationFrame(updateTimer)
    } catch (error) {
      console.error("Failed to start measurement:", error)
      setResult({
        success: false,
        message: error.message || "Failed to start measurement",
      })
    } finally {
      setModalLoading(false)
    }
  }

  const handleStop = async () => {
    console.log("Stopping measurement...")
    setModalLoading(true)

    const mobileDeviceInfo = devices.map(device => ({
      connectionId: device.connectionId,
      camera: device.camera,
      bleEnabled: device.bleDevice === "On",
      mode: device.mode,
      ppgDeviceOn: device.ppgDeviceOn
    }))

    const requestBody = {
      subjectId: measurementData.subjectId,
      recordingId: measurementData.recordingId,
      mobileDeviceInfo: mobileDeviceInfo,
    }

    try {
      const response = await axios.post(
        `${BASE_URL}web/measurementEnd`,
        requestBody
      )
      cameras.forEach(async camera => {
        const hostname = camera.details.hostname
        if (camera.type === "axis") {
          console.log("Stopping recording for camera", hostname)
          await AxisUtility.stopRecording(hostname, recordingProfileIDsMap[hostname])
        } else if (camera.type === "hanwha") {
          console.log("Stopping recording for camera", hostname)
          await HanwhaUtility.stopHanwhaRecording(camera.details.hostname);
        }
      })

      console.log("Measurement stopped:", response.data)
      setResult({
        success: true,
        message: "Measurement stopped successfully!",
      })
      setDashboardData(response.data.devices)
      setShowCloseButton(true)
      clearInterval(timerRef.current)
      setCurrentPage(4) // Navigate to Blood Sugar Level page
    } catch (error) {
      console.error("Failed to stop measurement:", error)
      setResult({
        success: false,
        message: error.message || "Failed to stop measurement",
      })
    } finally {
      setModalLoading(false)
    }
  }

  const handleCancel = async () => {
    setModalLoading(true)
    console.log("Measurement is cancelled")

    const mobileDeviceInfo = devices.map(device => ({
      connectionId: device.connectionId,
    }))

    const requestBody = {
      subjectId: measurementData.subjectId,
      recordingId: measurementData.recordingId,
      mobileDeviceInfo: mobileDeviceInfo,
    }

    try {
      const response = await axios.post(
        `${BASE_URL}web/measurementCancel`,
        requestBody
      )
      console.log("Measurement cancelled:", response.data)
      setModalLoading(false)
    } catch (error) {
      console.error("Failed to cancel measurement:", error)
      setModalLoading(false)
    }
    setNoteModalTitle("Why did you cancel the measurement?")
    setShowNoteModal(true)

    toggle()
  }
  const handleAddNote = () => {
    setNoteModalTitle("Add note for this recording")
    setShowNoteModal(true)
  }

  const handleCancelFirst = async () => {
    toggle()
  }

  const handleCancelAnnotation = async () => {
    try {
      await axios.get(`${BASE_URL}web/managerLeftSession`);
    } catch (error) {
      console.error('Error during session cancellation:', error);
    }
    toggle()
  }

  const handleNoteSaved = () => {
    // Handle any actions after the note is saved
    setShowNoteModal(false)
    if (currentPage === 2) {
      toggle() // Close the measurement modal if it was canceled
    }
  }

  useEffect(() => {
    if (currentPage === 4) {
      // Reset state when the Blood Sugar Level page is opened
      setHasMeasured(null)
      setLastAte("")
      setGlucoseLevel("")
      setErrors([])
      setResult(null)
      setSystolic("")
      setDiastolic("")
    }
  }, [currentPage])

  const validate = () => {
    const newErrors = []
    if (hasMeasured === null) {
      newErrors.push({
        field: "measured",
        message: "Please indicate if you have measured your blood sugar level.",
      })
    }
    if (hasMeasured && !lastAte) {
      newErrors.push({
        field: "lastAte",
        message: "Please indicate when you last ate.",
      })
    }
    if (
      hasMeasured &&
      (!glucoseLevel || isNaN(glucoseLevel) || glucoseLevel < 0)
    ) {
      newErrors.push({
        field: "glucoseLevel",
        message: "Please enter a valid, non-negative glucose level.",
      })
    }
    if ((systolic && !diastolic) || (!systolic && diastolic)) {
      newErrors.push({
        field: "bloodPressure",
        message:
          "Please enter both systolic and diastolic pressure or leave both empty.",
      })
    }
    if (systolic && (isNaN(systolic) || systolic < 40 || systolic > 300)) {
      newErrors.push({
        field: "systolic",
        message: "Systolic pressure must be between 40 and 300.",
      })
    }
    if (diastolic && (isNaN(diastolic) || diastolic < 10 || diastolic > 200)) {
      newErrors.push({
        field: "diastolic",
        message: "Diastolic pressure must be between 10 and 200.",
      })
    }
    setErrors(newErrors)
    return newErrors.length === 0
  }

  const handleSubmit = async () => {
    if (validate()) {
      const data = {
        subjectId: measurementData.subjectId,
        recordingId: measurementData.recordingId,
        hasMeasured,
        lastAte,
        bloodSugarLevel: hasMeasured ? Number(glucoseLevel) : null,
        systolic: systolic ? Number(systolic) : null,
        diastolic: diastolic ? Number(diastolic) : null,
      }
      setBloodModalLoading(true)
      const subjectIdx = data.subjectId ?? null
      const recordingIdx = data.recordingId ?? null
      const didBloodSugarLevelMeasure = data.hasMeasured ? "yes" : "no"

      const lastAtex =
        {
          "last 1 hour": "Last_1_hour",
          "1-2 hours": "1-2",
          "2-3 hours": "2-3",
          "3-4 hours": "3-4",
          "4-5 hours": "4-5",
          "5+ hours": "5+",
        }[data.lastAte] || null

      const bloodSugarLevelx = parseFloat(data.bloodSugarLevel)

      try {
        const res = await axios.post(`${BASE_URL}web/bloodSugar`, {
          subjectId: subjectIdx,
          recordingId: recordingIdx,
          didBloodSugarLevelMeasure: didBloodSugarLevelMeasure,
          lastMealTime: lastAtex,
          bloodSugarLevel: bloodSugarLevelx,
        })

        if (data.systolic && data.diastolic) {
          const bloodPressureRes = await axios.post(
            `${BASE_URL}web/manualBloodPressure`,
            {
              subjectId: subjectIdx,
              systolic: data.systolic,
              diastolic: data.diastolic,
            }
          )
        }

        setBloodModalLoading(false)
        setResult({
          success: true,
          message:
            "Blood sugar level and blood pressure recorded successfully.",
        })
      } catch (err) {
        console.log("Error making request", err)
        setBloodModalLoading(false)
        setResult({
          success: false,
          message: "Failed to record blood sugar level and blood pressure.",
        })
      }
    }
  }
  
 

  // Check if all devices are in DISCONNECT status
  const allDevicesDisconnected =
    (devices.length === 0 && cameras.length === 0) ||
    (devices.length > 0 &&
      devices.every(
        device =>
          messages[device.connectionId] === "DISCONNECTED" ||
          messages[device.connectionId]?.state === "CANCELLED" ||
          messages[device.connectionId]?.state === "DONE"
      ))
  // If all the devices are DISCONNECTED, CANCELLED or DONE, go to AfterRecording Page.
  useEffect(() => {
    //console.log("ALL DEVICES DISCONNECTED, CANCELLED or DONE")
    if (allDevicesDisconnected && currentPage == 3 && cameras?.length === 0) {
      setCurrentPage(4)
    }

  }, [allDevicesDisconnected])

  // Check if all devices are either "distance" or "ready"
  const allDevicesReady =
    (devices.length === 0 && cameras.length > 0) ||
    devices.every(device =>
      ["DISTANCE", "READY","POSITION"].includes(messages[device.connectionId]?.state)
    )

  const getFieldError = field => {
    const error = errors.find(error => error.field === field)
    return error ? error.message : null
  }
  const currentRecordingNumber = selectedSubject ? selectedSubject.todayRecordings + 1 : 1;


  return (
    <>
      <Modal isOpen={isOpen} size="xl" backdrop="static" keyboard={false}>
        <ModalHeader className="d-flex justify-content-between align-items-center w-100">
          <p className="mb-0">New Measurement</p>
          <Buttons
            currentPage={currentPage}
            handleCancelAnnotation={handleCancelAnnotation}
            handleCancelFirst={handleCancelFirst}
            handleProceed={handleProceed}
            handleStart={handleStart}
            handleStop={handleStop}
            handleCancel={handleCancel}
            handleProceedLast={handleProceedLast}
            handleSubmit={handleSubmit}
            result={result}
            modalLoading={modalLoading}
            allDevicesReady={allDevicesReady}
            toggle={toggle}
            checks={checks}
          />
        </ModalHeader>

        {modalLoading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "200px" }}
          >
            <ClipLoader color={"#727cf5"} loading={modalLoading} size={50} />
          </div>
        ) : (
          <ModalBody>
            {currentPage === 0 ? (
            <BloodPressureAnnotation 
            onSkip={() => setCurrentPage(1)}
            onContinue={() => setCurrentPage(1)}
            selectedSubject={selectedSubject}
            annotationMessages={annotationMessages}
            setAnnotationMessages={setAnnotationMessages}
            recordingId={recordingId}
            />
          ) : currentPage === 1 ? (
              // The first page to set up the Camera and BLE Device
              <SetupRecording
                selectedSubject={selectedSubject}
                currentRecordingNumber={currentRecordingNumber}
                devices={devices}
                onSettingChange={onSettingChange}
                messages={messages}
                cameras={cameras}
                result={result}
                warningComponent={warningComponent}
              />
            ) : currentPage === 2 ? (
              // The second page to show the devices status just before recording
              <BeforeRecording
                selectedSubject={selectedSubject}
                currentRecordingNumber={currentRecordingNumber}
                devices={devices}
                cameras={cameras}
                result={result}
                messages={messages}
                warningComponent={warningComponent}
              />
            ) : currentPage === 3 ? (
              // The third page to show the devices status during recording
              <DuringRecording
                selectedSubject={selectedSubject}
                currentRecordingNumber={currentRecordingNumber}
                devices={devices}
                messages={messages}
                cameras={cameras}
                timer={timer}
              />
            ) : currentPage == 4 ? (
              // The fourth page to show the devices status just after recording
              <AfterRecording
                selectedSubject={selectedSubject}
                currentRecordingNumber={currentRecordingNumber}
                devices={devices}
                messages={messages}
                cameras={cameras}
              />
            ) : (
              // Page 4: Blood Sugar Level
              <BloodSugarLevel
                bloodModalLoading={bloodModalLoading}
                result={result}
                hasMeasured={hasMeasured}
                setHasMeasured={setHasMeasured}
                lastAte={lastAte}
                setLastAte={setLastAte}
                glucoseLevel={glucoseLevel}
                setGlucoseLevel={setGlucoseLevel}
                systolic={systolic}
                setSystolic={setSystolic}
                diastolic={diastolic}
                setDiastolic={setDiastolic}
                getFieldError={getFieldError}
              />
            )}
            {currentPage === 5 && result && (
              <Button color="info" onClick={handleAddNote} className="mt-3">
                Add note for this recording
              </Button>
            )}
          </ModalBody>
        )}

        <ModalFooter>
          <Buttons
             currentPage={currentPage}
             handleCancelAnnotation={handleCancelAnnotation}
             handleCancelFirst={handleCancelFirst}
             handleProceed={handleProceed}
             handleStart={handleStart}
             handleStop={handleStop}
             handleCancel={handleCancel}
             handleProceedLast={handleProceedLast}
             handleSubmit={handleSubmit}
             result={result}
             modalLoading={modalLoading}
             allDevicesReady={allDevicesReady}
             toggle={toggle}
             checks={checks}
          />
        </ModalFooter>
      </Modal>

      <NoteModal
        isOpen={showNoteModal}
        toggle={() => setShowNoteModal(false)}
        recordingId={measurementData.recordingId}
        onNoteSaved={handleNoteSaved}
        title={noteModalTitle}
      />
    </>
  )
}

export default NewMeasurementModal
